"use client";
import {
  Accordion,
  Anchor,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import CreditCardOffRounded from "@mui/icons-material/CreditCardOffRounded";
import DesktopMacRounded from "@mui/icons-material/DesktopMacRounded";
import EventRepeatRounded from "@mui/icons-material/EventRepeatRounded";
import GppGoodRounded from "@mui/icons-material/GppGoodRounded";
import PaidRounded from "@mui/icons-material/PaidRounded";
import PhonelinkRounded from "@mui/icons-material/PhonelinkRounded";
import StackedBarChartRounded from "@mui/icons-material/StackedBarChartRounded";
import SyncDisabledRounded from "@mui/icons-material/SyncDisabledRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { getFaqSnippets } from "@utils/metaTags";
import Link from "next/link";
import FaqItem, { faqItem } from "./FaqItem";

export default function FaqModule() {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("light");
  const faqTextProps = {
    c: colorScheme === "dark" ? theme.colors.dark[1] : "darkblue",
    size: "md",
  };

  const faqs: faqItem[] = [
    {
      question: "What is Tiber Tutor",
      answer: (
        <>
          <Text {...faqTextProps}>
            Tiber Tutor is the only IB dedicated educational platform to include
            a full range of revision resources: videos, notes, flashcards,
            cram-sheets, questions and mark schemes.
          </Text>
          <Text {...faqTextProps}>
            No other online IB science resource comes close to us in quality,
            quantity, breadth or depth of resources. They often leverage
            questions from other exam-boards, or AI generated content. We do
            neither.
          </Text>
        </>
      ),
    },
    {
      icon: StackedBarChartRounded,
      question: "What resources does Tiber Tutor have",
      answer: (
        <>
          <Text {...faqTextProps}>
            We have: professional animated videos, reliable revision notes,
            3,700 accompanying flashcards, and over 5,300 exam questions.
          </Text>
          <Text {...faqTextProps}>
            With over 5,300 questions, each with a detailed mark scheme, you can
            build your understanding rather than just receive a percentage.
          </Text>
        </>
      ),
      color: "biology",
    },
    {
      icon: EventRepeatRounded,
      question: "Are Tiber Tutor resources up-to-date",
      answer: (
        <Text {...faqTextProps}>
          Revising from out of date sources is damaging. Our team of experts
          works tirelessly to update and curate new resources in line with the
          latest IB syllabus.
        </Text>
      ),
      color: "chemistry",
    },
    {
      icon: CreditCardOffRounded,
      question: "Can I trial the content for free",
      answer: (
        <>
          <Text {...faqTextProps}>
            With a free 7-day trial, you can access everything completely free.
            No payment information required.
          </Text>
          <Text {...faqTextProps}>
            For further access, our pricing scales based on the number of
            subjects you desire. We offer memberships on a monthly, 6-monthly,
            yearly, or full-course basis (24 months). These can be found at the{" "}
            <Anchor component={Link} href={"/pricing"}>
              bottom of our homepage
            </Anchor>
            .
          </Text>
        </>
      ),
      color: "physics",
    },
    {
      icon: SyncDisabledRounded,
      question: "How do I cancel my subscription",
      answer: (
        <Text {...faqTextProps}>
          We allow all users to cancel their membership from their profile menu
          in just 2 clicks. If you change your mind, you can re-subscribe
          immediately.
        </Text>
      ),
      color: "maths",
    },
    {
      icon: YouTubeIcon,
      question: "Why Tiber Tutor over free services like Youtube",
      answer: (
        <>
          <Text {...faqTextProps}>
            Watching videos are fantastic to learn new concepts. We even have
            our own{" "}
            <Anchor
              href={"https://www.youtube.com/channel/UC-oRhe5czsZM3JdTX_vm2Eg"}
            >
              Youtube channel
            </Anchor>
            ! However, engagement using flashcards and interactive questions
            massively boosts performance.
          </Text>
          <Text {...faqTextProps}>
            <Anchor component={Link} href={"/"}>
              Tiber Tutor
            </Anchor>{" "}
            is the only online platform with detailed interactivty and user
            metrics linked to our videos, notes, flashcards and questions.
          </Text>
        </>
      ),
      color: "biology",
    },
    {
      icon: PhonelinkRounded,
      question: "Can I login on multiple devices",
      answer: (
        <Text {...faqTextProps}>
          To help maintain all our users security, each user can login with two
          devices at any time. If this limit is reached, devices will be
          forcibly logged out.
        </Text>
      ),
      color: "chemistry",
    },
    {
      icon: PaidRounded,
      question: "What currencies does Tiber Tutor accept",
      answer: (
        <Text {...faqTextProps}>
          We accept USD, GBP and EUR. To ensure everyone can join, we accept
          nearly all payment providers through{" "}
          <Anchor href={"https://stripe.com/"}>Stripe</Anchor>, the leading
          payment provider gloablly.
        </Text>
      ),
      color: "physics",
    },
    {
      icon: DesktopMacRounded,
      question: "What devices work with Tiber Tutor",
      answer: (
        <Text {...faqTextProps}>
          We have worked hard to ensure every device is compatible with Tiber
          Tutor. If you find any issues, please let us know.
        </Text>
      ),
      color: "maths",
    },
    {
      icon: GppGoodRounded,
      question: "How do I verify my account",
      answer: (
        <>
          <Text {...faqTextProps}>
            You will receive an email with a link. Simply click it.
          </Text>
          <Text {...faqTextProps}>
            If you are having issues, contact us using the form at the bottom of
            our site, or by{" "}
            <Anchor href="mailto:info@tibertutor.com">email</Anchor>.
          </Text>
        </>
      ),
    },
  ];
  return (
    <Accordion variant="separated" mt={`calc(3*${theme.spacing.xl})`}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getFaqSnippets()) }}
      />
      {faqs.map((faq, indx) => (
        <FaqItem key={faq.question} indx={indx} faq={faq} />
      ))}
    </Accordion>
  );
}
